import {createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {usePaginate} from "../PaginateProvider";
import {ProposalsReceiptsGroupsService} from "../../services/Proposals/ProposalsReceiptsGroupsService";

interface ContextProps {
    proposalsReceiptsGroups: any[],
    setProposalsReceiptsGroups: Dispatch<SetStateAction<any[]>>,
    isLoading: boolean,
    error: string,
    params: any,
    setParams: Dispatch<SetStateAction<any>>,
    handleList: () => Promise<any>,
    handleGet: (id?: number) => Promise<any>,
    handleSave: (payload: any) => Promise<any>,
    handleInative: (id: number | null) => Promise<any>,
}

export const ProposalsReceiptsGroupsContext = createContext<ContextProps>({} as ContextProps);

interface ProposalsReceiptsGroupsProviderProps {
    children: ReactNode
}

export const ProposalsReceiptsGroupsProvider = ({children}: ProposalsReceiptsGroupsProviderProps) => {
    const [proposalsReceiptsGroups, setProposalsReceiptsGroups] = useState<any[]>([]);
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [params, setParams] = useState<any>({page: 1});

    const {setPagesPaginate} = usePaginate();
    const location = useLocation();
    const service = new ProposalsReceiptsGroupsService();

    const handleList = useCallback(async () => {
        if (!location.pathname.includes('/proposal-track/receipt/groups/list')) {
            return false;
        }

        try {
            setIsLoading(true);
            const [_Response, _Error] = await service.list(params);

            if (!!_Error) {
                setIsLoading(false);
                return setError(_Response || _Error);
            }

            setProposalsReceiptsGroups(_Response.data);
            setPagesPaginate(_Response.pages);

            return setIsLoading(false);
        } catch (e) {
            console.warn(e);
            return setIsLoading(false);
        }
    }, [location.pathname, params]);

    const handleGet = async (id?: number) => {
        setIsLoading(true);

        try {
            const [_Response, _Error] = await service.get({id});

            if (!!_Error) {
                setIsLoading(false);
                return setError(_Response || _Error);
            }

            setProposalsReceiptsGroups(_Response.data);

            return setIsLoading(false);
        } catch (e) {
            console.warn(e);
            return setIsLoading(false);
        }
    };


    const handleSave = async (payload: any) => {
        setIsLoading(true);

        try {
            const [_Response, _Error] = await service.save(payload);

            if (!!_Error) {
                setIsLoading(false);
                setError(_Response || _Error);
                return _Error;
            }


            setIsLoading(false);
            return "";
        } catch (e) {
            console.warn(e);
            setIsLoading(false);
            return e;
        }
    }

    const handleInative = async (id: number | null) => {
        setIsLoading(true);

        try {
            const [_Response, _Error] = await service.delete(id);

            if (!!_Error) {
                setIsLoading(false);
                setError(_Response || _Error);
                return _Error;
            }


            setIsLoading(false);
            return "";
        } catch (e) {
            console.warn(e);
            setIsLoading(false);
            return e;
        }
    }


    useEffect(() => {
        handleList().then();
    }, [handleList]);

    return (
        <ProposalsReceiptsGroupsContext.Provider value={{
            proposalsReceiptsGroups,
            setProposalsReceiptsGroups,
            error,
            params,
            setParams,
            handleList,
            handleGet,
            isLoading,
            handleSave,
            handleInative,
        }}>
            {children}
        </ProposalsReceiptsGroupsContext.Provider>
    )
}

export const useProposalsReceiptsGroups = () => useContext(ProposalsReceiptsGroupsContext);
